import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  DoneRounded as DoneIcon,
  EditRounded as EditIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import * as dates from "common/dates";
import Component from "components/Component";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import InlineSearch from "components/common/InlineSearch";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import CauseCreateRecordDialog from "components/reports/CauseCreateRecordDialog";
import {
  saveCauseWorkbookChanges,
  getPriorSubmissionId,
} from "components/reports/causeWorkbookUpdater";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as bffService from "services/bffService";
import * as submissionsService from "services/submissionsService";

const CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS =
  "cause-workbook-current-year-premium";
const ARK_LIMIT_MILLIONS = 5;
const GROUP_TOTALS_ROW_ID = "__cause-group-totals__";
const GROUP_TOTAL_FIELDS = [
  "ueGlxPremiumPreviousYear",
  "ueGlxPremiumCurrentYear",
  "ueEllPremiumPreviousYear",
  "ueEllPremiumCurrentYear",
  "ueCombinedPremiumPreviousYear",
  "ueCombinedPremiumCurrentYear",
  "munichRequiredPremium",
  "evanstonPremium",
  "arkTotalPremium",
  "arkErp",
  "totalPremiumWithSerp",
];
const EDIT_FIELD_SX = {
  marginTop: 0,
  "& .MuiInputBase-root": {
    fontSize: "inherit",
    lineHeight: "inherit",
    height: "100%",
    alignItems: "center",
    transform: "translateY(2px)",
  },
  "& .MuiInputBase-input": {
    fontSize: "inherit",
    lineHeight: "inherit",
    paddingTop: "3px",
    paddingBottom: "1px",
  },
};

const getSubmissionId = (submission) => {
  if (!submission) return null;
  if (submission.submissionId) return submission.submissionId;
  if (submission.id) return submission.id;
  if (submission.filename && submission.filename.endsWith(".json")) {
    return submission.filename.replace(/\.json$/i, "");
  }
  return null;
};

const calculatePpm = (premium, limitMillions) => {
  if (premium == null || limitMillions == null || limitMillions === 0) {
    return null;
  }
  return premium / limitMillions;
};

const combinePremiums = (firstPremium, secondPremium) => {
  if (firstPremium == null && secondPremium == null) {
    return null;
  }
  return (firstPremium || 0) + (secondPremium || 0);
};

const calculatePremiumIncreasePct = (previousPremium, currentPremium) => {
  if (
    previousPremium == null ||
    previousPremium === 0 ||
    currentPremium == null
  ) {
    return null;
  }
  return (currentPremium / previousPremium - 1) * 100;
};

const normalizeGroupName = (value) => (value || "").trim();

const sumNumericField = (rows, field) => {
  const values = rows
    .map((row) => row[field])
    .filter((value) => typeof value === "number" && Number.isFinite(value));
  if (values.length === 0) {
    return null;
  }
  return values.reduce((sum, value) => sum + value, 0);
};

const buildGroupTotalsRow = (rows, selectedGroup) => {
  const totals = GROUP_TOTAL_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field]: sumNumericField(rows, field),
    }),
    {}
  );

  return {
    id: GROUP_TOTALS_ROW_ID,
    insuredName: "Totals",
    effectiveDate: "",
    group: selectedGroup,
    ...totals,
  };
};

const applyDerivedValues = (row) => {
  const ueCombinedPremiumPreviousYear = combinePremiums(
    row.ueGlxPremiumPreviousYear,
    row.ueEllPremiumPreviousYear
  );
  const ueCombinedPremiumCurrentYear = combinePremiums(
    row.ueGlxPremiumCurrentYear,
    row.ueEllPremiumCurrentYear
  );
  const arkPpm = calculatePpm(row.arkTotalPremium, ARK_LIMIT_MILLIONS);
  const arkRelativeToEvanston =
    arkPpm == null || row.evanstonPpm == null || row.evanstonPpm === 0
      ? null
      : (arkPpm / row.evanstonPpm) * 100;

  return {
    ...row,
    ueGlxPpm: calculatePpm(row.ueGlxPremiumCurrentYear, row.ulLimitMillions),
    ueEllPpm: calculatePpm(row.ueEllPremiumCurrentYear, row.ulLimitMillions),
    ueCombinedPremiumPreviousYear: ueCombinedPremiumPreviousYear,
    ueCombinedPremiumCurrentYear: ueCombinedPremiumCurrentYear,
    ueCombinedUlPpm: calculatePpm(
      ueCombinedPremiumCurrentYear,
      row.ulLimitMillions
    ),
    ueGlxPremiumIncrease: calculatePremiumIncreasePct(
      row.ueGlxPremiumPreviousYear,
      row.ueGlxPremiumCurrentYear
    ),
    ueEllPremiumIncrease: calculatePremiumIncreasePct(
      row.ueEllPremiumPreviousYear,
      row.ueEllPremiumCurrentYear
    ),
    ueCombinedPremiumIncrease: calculatePremiumIncreasePct(
      ueCombinedPremiumPreviousYear,
      ueCombinedPremiumCurrentYear
    ),
    arkPpm: arkPpm,
    arkRelativeToEvanston: arkRelativeToEvanston,
    arkErp: arkPpm,
    totalPremiumWithSerp:
      row.arkTotalPremium != null && arkPpm != null
        ? row.arkTotalPremium + arkPpm
        : null,
  };
};

const getPricingMetrics = (submission) => {
  if (!submission) {
    return {
      ulLimitMillions: null,
      ueGlxPremium: null,
      ueGlxPpm: null,
      ueEllPremium: null,
      ueEllPpm: null,
      ueCombinedPremium: null,
      ueCombinedUlPpm: null,
      munichRequiredPremium: null,
      munichPpm: null,
      munichPpmRelativeToUl: null,
      evanstonPpm: null,
      evanstonPpmRelativeToUl: null,
      evanstonPremium: null,
      actualPremium15M: null,
      arkPpm: null,
      arkTotalPremium: null,
      arkRelativeToEvanston: null,
      arkErp: null,
      totalPremiumWithSerp: null,
    };
  }

  const layers = submission.state?.pricing?.layers;
  const tower = submission.state?.pricing?.tower;

  const ueGlxLayer = layers?.find(
    (l) => l.coverage === "GLX" && l.carrier === "United Educators"
  );
  const ueEllLayer = layers?.find(
    (l) => l.coverage === "ELL" && l.carrier === "United Educators"
  );
  const munichLayer = layers?.find(
    (l) => l.carrier?.toLowerCase() === "munich"
  );
  const evanstonLayer = layers?.find(
    (l) => l.carrier?.toLowerCase() === "evanston"
  );

  const towerLayer0 = tower?.layers?.[0]; // United Educators combined
  const towerLayer1 = tower?.layers?.[1]; // Munich
  const towerLayer2 = tower?.layers?.[2]; // Evanston
  const towerLayer3 = tower?.layers?.[3]; // Ark

  const ulLimitMillions = ueGlxLayer?.limit ? ueGlxLayer.limit / 1000000 : null;

  const ueGlxPremium = ueGlxLayer?.grossPremium || null;
  const ueGlxPpm = calculatePpm(ueGlxPremium, ulLimitMillions);

  const ueEllPremium = ueEllLayer?.grossPremium || null;
  const ueEllPpm = calculatePpm(ueEllPremium, ulLimitMillions);

  const combinedPremium =
    towerLayer0?.grossPremium || (ueGlxPremium || 0) + (ueEllPremium || 0);

  const combinedUlPpm = calculatePpm(combinedPremium, ulLimitMillions);

  const munichRelativeToUl = towerLayer1?.grossRPMRelativeToLayerBelow || null;
  const munichLimit = towerLayer1?.limit ? towerLayer1.limit / 1000000 : null;
  const munichLayerLimit = munichLayer?.limit
    ? munichLayer.limit / 1000000
    : null;
  const munichShare = towerLayer1?.shares?.[0];
  const munichPpm =
    towerLayer1?.grossRPM ||
    munichShare?.grossRPM ||
    (combinedUlPpm && munichRelativeToUl
      ? combinedUlPpm * munichRelativeToUl
      : null) ||
    (munichLayer?.grossPremium && munichLayerLimit
      ? munichLayer.grossPremium / munichLayerLimit
      : null);
  const munichRequiredPremium =
    towerLayer1?.grossPremium ||
    munichShare?.shareOfGrossPremium ||
    munichLayer?.grossPremium ||
    (munichPpm && munichLimit ? munichPpm * munichLimit : null);

  const evanstonRelativeToMunich =
    towerLayer2?.grossRPMRelativeToLayerBelow || null;
  const evanstonLimit = towerLayer2?.limit ? towerLayer2.limit / 1000000 : null;
  const evanstonLayerLimit = evanstonLayer?.limit
    ? evanstonLayer.limit / 1000000
    : null;
  const evanstonShare = towerLayer2?.shares?.[0];
  const evanstonPpm =
    towerLayer2?.grossRPM ||
    evanstonShare?.grossRPM ||
    (munichPpm && evanstonRelativeToMunich
      ? munichPpm * evanstonRelativeToMunich
      : null) ||
    (evanstonLayer?.grossPremium && evanstonLayerLimit
      ? evanstonLayer.grossPremium / evanstonLayerLimit
      : null);
  const evanstonPremium =
    towerLayer2?.grossPremium ||
    evanstonShare?.shareOfGrossPremium ||
    evanstonLayer?.grossPremium ||
    (evanstonPpm && evanstonLimit ? evanstonPpm * evanstonLimit : null);

  const arkRelativeToEvanston =
    towerLayer3?.grossRPMRelativeToLayerBelow || null;
  const arkLimit = towerLayer3?.limit ? towerLayer3.limit / 1000000 : null;
  const arkShare = towerLayer3?.shares?.[0];
  const arkPpm =
    towerLayer3?.grossRPM ||
    arkShare?.grossRPM ||
    (evanstonPpm && arkRelativeToEvanston
      ? evanstonPpm * arkRelativeToEvanston
      : null);
  const arkTotalPremium =
    towerLayer3?.grossPremium ||
    arkShare?.shareOfGrossPremium ||
    (arkPpm && arkLimit ? arkPpm * arkLimit : null);

  return {
    ulLimitMillions: ulLimitMillions,
    ueGlxPremium: ueGlxPremium,
    ueGlxPpm: ueGlxPpm,
    ueEllPremium: ueEllPremium,
    ueEllPpm: ueEllPpm,
    ueCombinedPremium: combinedPremium,
    ueCombinedUlPpm: combinedUlPpm,
    munichRequiredPremium: munichRequiredPremium,
    munichPpm: munichPpm,
    munichPpmRelativeToUl: munichRelativeToUl ? munichRelativeToUl * 100 : null,
    evanstonPpm: evanstonPpm,
    evanstonPpmRelativeToUl: evanstonRelativeToMunich
      ? evanstonRelativeToMunich * 100
      : null,
    evanstonPremium: evanstonPremium,
    actualPremium15M:
      munichRequiredPremium && evanstonPremium
        ? munichRequiredPremium + evanstonPremium
        : null,
    arkPpm: arkPpm,
    arkTotalPremium: arkTotalPremium,
    arkRelativeToEvanston: arkRelativeToEvanston
      ? arkRelativeToEvanston * 100
      : null,
    arkErp: arkPpm,
    totalPremiumWithSerp:
      arkTotalPremium && arkPpm ? arkTotalPremium + arkPpm : null,
  };
};

const extractDataFromSubmission = (submission, priorSubmission) => {
  if (!submission) return null;

  const program = submission.state?.input?.program;
  const rowId = getSubmissionId(submission);

  if (!rowId) {
    return null;
  }

  const currentMetrics = getPricingMetrics(submission);
  const priorMetrics = getPricingMetrics(priorSubmission);
  const inceptionValue = program?.inception || submission.inception;
  const inceptionDate = dates.parseDateValue(inceptionValue);
  const inceptionTime = inceptionDate ? inceptionDate.getTime() : null;
  const effectiveYear =
    inceptionDate && !Number.isNaN(inceptionTime)
      ? inceptionDate.getFullYear()
      : null;
  const programMunichBoundPremiumPreviousYear =
    program?.munichBoundPremiumPreviousYear ??
    program?.munichBoundPremium2024 ??
    null;
  const programEvanstonBoundPremiumPreviousYear =
    program?.evanstonBoundPremiumPreviousYear ??
    program?.evanstonBoundPremium2024 ??
    null;
  const munichBoundPremiumPreviousYear =
    programMunichBoundPremiumPreviousYear ?? priorMetrics.munichRequiredPremium;
  const evanstonBoundPremiumPreviousYear =
    programEvanstonBoundPremiumPreviousYear ?? priorMetrics.evanstonPremium;
  const munichEvanstonBoundPremiumPreviousYear =
    munichBoundPremiumPreviousYear != null &&
    evanstonBoundPremiumPreviousYear != null
      ? munichBoundPremiumPreviousYear + evanstonBoundPremiumPreviousYear
      : priorMetrics.actualPremium15M;
  const princetonPremiumIncrease =
    munichBoundPremiumPreviousYear && currentMetrics.munichRequiredPremium
      ? (currentMetrics.munichRequiredPremium / munichBoundPremiumPreviousYear -
          1) *
        100
      : null;
  const markelPremiumIncrease =
    evanstonBoundPremiumPreviousYear && currentMetrics.evanstonPremium
      ? (currentMetrics.evanstonPremium / evanstonBoundPremiumPreviousYear -
          1) *
        100
      : null;
  const ue15MPremiumIncrease =
    munichEvanstonBoundPremiumPreviousYear && currentMetrics.actualPremium15M
      ? (currentMetrics.actualPremium15M /
          munichEvanstonBoundPremiumPreviousYear -
          1) *
        100
      : null;

  const ueGlxPremiumPreviousYear =
    program?.ueGlxPremiumPreviousYear ?? priorMetrics.ueGlxPremium;
  const ueEllPremiumPreviousYear =
    program?.ueEllPremiumPreviousYear ?? priorMetrics.ueEllPremium;
  const ueGlxPremiumCurrentYear =
    program?.ueGlxPremiumCurrentYear ?? currentMetrics.ueGlxPremium;
  const ueEllPremiumCurrentYear =
    program?.ueEllPremiumCurrentYear ?? currentMetrics.ueEllPremium;
  const arkTotalPremium =
    program?.arkTotalPremiumCurrentYear ?? currentMetrics.arkTotalPremium;

  return applyDerivedValues({
    id: rowId,
    insuredName: program?.insured || submission.insured || "-",
    effectiveDate:
      inceptionDate && !Number.isNaN(inceptionTime)
        ? inceptionDate.toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "2-digit",
          })
        : "-",
    effectiveYear: effectiveYear,
    group: normalizeGroupName(program?.reference),
    ulLimitMillions: currentMetrics.ulLimitMillions,
    ueGlxPremiumPreviousYear: ueGlxPremiumPreviousYear,
    ueGlxPremiumCurrentYear: ueGlxPremiumCurrentYear,
    ueEllPremiumPreviousYear: ueEllPremiumPreviousYear,
    ueEllPremiumCurrentYear: ueEllPremiumCurrentYear,
    munichRequiredPremium: currentMetrics.munichRequiredPremium,
    munichPpm: currentMetrics.munichPpm,
    munichPpmRelativeToUl: currentMetrics.munichPpmRelativeToUl,
    evanstonPpm: currentMetrics.evanstonPpm,
    evanstonPpmRelativeToUl: currentMetrics.evanstonPpmRelativeToUl,
    evanstonPremium: currentMetrics.evanstonPremium,
    actualPremium15M: currentMetrics.actualPremium15M,
    munichBoundPremiumPreviousYear: munichBoundPremiumPreviousYear,
    evanstonBoundPremiumPreviousYear: evanstonBoundPremiumPreviousYear,
    munichEvanstonBoundPremiumPreviousYear: munichEvanstonBoundPremiumPreviousYear,
    princetonPremiumIncrease: princetonPremiumIncrease,
    markelPremiumIncrease: markelPremiumIncrease,
    ue15MPremiumIncrease: ue15MPremiumIncrease,
    arkTotalPremium: arkTotalPremium,
  });
};

const InsuredNameCell = memo(function InsuredNameCell({ id, value, onCommit }) {
  const [draftValue, setDraftValue] = useState(value || "");
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      setDraftValue(value || "");
    }
  }, [value]);

  const handleBlur = useCallback(() => {
    const normalizedValue = draftValue || "";
    if ((value || "") !== normalizedValue) {
      onCommit(id, normalizedValue);
    }
  }, [draftValue, id, onCommit, value]);

  return (
    <MuiTextField
      value={draftValue}
      onChange={(event) => setDraftValue(event.target.value)}
      onBlur={handleBlur}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      size={"small"}
      fullWidth
      variant={"standard"}
      sx={EDIT_FIELD_SX}
    />
  );
});

const CauseWorkbookPage = ({ isFullWidth, onToggleFullWidth }) => {
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const pendingCreatedRowsRef = useRef(new Map());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [manualGroupOptions, setManualGroupOptions] = useState([]);
  const [addingGroupRowId, setAddingGroupRowId] = useState(null);
  const [newRowGroupValue, setNewRowGroupValue] = useState("");
  const [isLoadingRows, setIsLoadingRows] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: submissionsList,
    isLoading: isLoadingSubmissions,
    refetch: refetchSubmissions,
  } = submissionsService.useListSubmissionsQuery();

  const [
    retrieveSubmission,
  ] = submissionsService.useLazyRetrieveSubmissionQuery();

  const [patchSubmission] = submissionsService.usePatchSubmissionMutation();
  const [
    updateProgramSubmission,
  ] = bffService.useUpdateProgramSubmissionMutation();

  const handleOpenCreate = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDeleteSubmission = useCallback(
    async (submissionId) => {
      try {
        const submission = await retrieveSubmission({ submissionId }).unwrap();
        const priorSubmissionId = getPriorSubmissionId(submission);
        const idsToDelete = new Set(
          [submissionId, priorSubmissionId].filter(Boolean)
        );

        await Promise.all(
          Array.from(idsToDelete).map((id) =>
            patchSubmission({
              submissionId: id,
              patches: [
                {
                  path: "state.meta.deleted",
                  type: "REPLACE",
                  value: true,
                },
              ],
            }).unwrap()
          )
        );

        pendingCreatedRowsRef.current.delete(submissionId);
        if (priorSubmissionId) {
          pendingCreatedRowsRef.current.delete(priorSubmissionId);
        }
        setRows((prev) => prev.filter((r) => !idsToDelete.has(r.id)));
      } catch (error) {
        return;
      }
    },
    [patchSubmission, retrieveSubmission, setRows]
  );

  const handleOpenDeleteConfirm = useCallback((submissionId) => {
    setSubmissionToDelete(submissionId);
    setDeleteConfirmOpen(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false);
    setSubmissionToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!submissionToDelete) return;
    handleDeleteSubmission(submissionToDelete);
    handleCloseDeleteConfirm();
  }, [handleCloseDeleteConfirm, handleDeleteSubmission, submissionToDelete]);

  const handleInsuredNameCommit = useCallback((submissionId, insuredName) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === submissionId ? { ...row, insuredName } : row
      )
    );
  }, []);

  const loadCauseWorkbookRows = useCallback(async () => {
    if (!submissionsList) {
      setRows([]);
      setOriginalRows([]);
      setIsLoadingRows(isLoadingSubmissions);
      return;
    }

    const causeSubmissionIds = submissionsList
      .filter((submission) => submission.tags?.some((tag) => tag === "CAUSE"))
      .map((submission) => submission.submissionId);

    if (causeSubmissionIds.length === 0) {
      setRows([]);
      setOriginalRows([]);
      setIsLoadingRows(false);
      return;
    }

    setRows([]);
    setIsLoadingRows(true);
    try {
      const submissions = await Promise.all(
        causeSubmissionIds.map((submissionId) =>
          retrieveSubmission({ submissionId }).unwrap()
        )
      );

      const submissionsById = new Map(
        submissions
          .map((submission) => [getSubmissionId(submission), submission])
          .filter(([submissionId]) => submissionId)
      );
      const priorIdsToSkip = new Set();

      const extractedRows = submissions
        .map((submission) => {
          const priorSubmissionId = getPriorSubmissionId(submission);
          if (priorSubmissionId) {
            priorIdsToSkip.add(priorSubmissionId);
          }
          const priorSubmission = priorSubmissionId
            ? submissionsById.get(priorSubmissionId)
            : null;
          return extractDataFromSubmission(submission, priorSubmission);
        })
        .filter(Boolean)
        .filter((row) => !priorIdsToSkip.has(row.id));

      const extractedRowIds = new Set(extractedRows.map((row) => row.id));
      extractedRowIds.forEach((rowId) => {
        pendingCreatedRowsRef.current.delete(rowId);
      });

      const pendingRows = Array.from(
        pendingCreatedRowsRef.current.values()
      ).filter((row) => !extractedRowIds.has(row.id));

      const nextRows = [...pendingRows, ...extractedRows];
      setRows(nextRows);
      setOriginalRows(JSON.parse(JSON.stringify(nextRows)));
      setIsLoadingRows(false);
    } catch (error) {
      setIsLoadingRows(false);
    }
  }, [isLoadingSubmissions, retrieveSubmission, submissionsList]);

  useEffect(() => {
    loadCauseWorkbookRows();
  }, [loadCauseWorkbookRows]);

  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveCauseWorkbookChanges({
        rows,
        originalRows,
        retrieveSubmission,
        updateProgramSubmission,
        refetchSubmissions,
      });
      await loadCauseWorkbookRows();
    } catch (error) {
      alert(
        `Failed to save changes: ${
          error?.data?.message || error?.message || "Unknown error"
        }`
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    rows,
    originalRows,
    loadCauseWorkbookRows,
    retrieveSubmission,
    updateProgramSubmission,
    refetchSubmissions,
  ]);

  const groupOptions = useMemo(() => {
    const options = new Set(manualGroupOptions);
    rows.forEach((row) => {
      const normalized = normalizeGroupName(row.group);
      if (normalized) {
        options.add(normalized);
      }
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [manualGroupOptions, rows]);

  const handleStartAddingGroupForRow = useCallback((rowId, currentValue) => {
    setAddingGroupRowId(rowId);
    setNewRowGroupValue(currentValue || "");
  }, []);

  const handleCancelAddGroupForRow = useCallback(() => {
    setAddingGroupRowId(null);
    setNewRowGroupValue("");
  }, []);

  const handleConfirmAddGroupForRow = useCallback(
    (rowId) => {
      const normalized = normalizeGroupName(newRowGroupValue);
      if (!normalized) {
        return;
      }
      setManualGroupOptions((prev) =>
        prev.includes(normalized) ? prev : [...prev, normalized]
      );
      setRows((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, group: normalized } : r))
      );
      setAddingGroupRowId(null);
      setNewRowGroupValue("");
    },
    [newRowGroupValue]
  );

  const columns = useMemo(() => {
    return [
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 80,
        getActions: (params) => {
          if (params.id === GROUP_TOTALS_ROW_ID) {
            return [];
          }
          return [
            <GridActionsCellItem
              key={"open-submission"}
              icon={<OpenInNewIcon />}
              label={"Open submission in new tab"}
              onClick={() =>
                window.open(
                  `/submissions/${params.id}`,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            />,
            <GridActionsCellItem
              key={"delete"}
              icon={<DeleteIcon />}
              label={"Delete"}
              onClick={() => handleOpenDeleteConfirm(params.id)}
            />,
          ];
        },
      },
      {
        field: "insuredName",
        headerName: "Insured Name",
        type: "string",
        width: 120,
        editable: false,
        renderCell: (params) => {
          if (editing) {
            return (
              <InsuredNameCell
                id={params.id}
                value={params.value || ""}
                onCommit={handleInsuredNameCommit}
              />
            );
          }
          return params.value;
        },
      },
      {
        field: "effectiveDate",
        headerName: "Effective Date",
        type: "string",
        width: 120,
        editable: false,
        renderCell: (params) => {
          if (editing) {
            const isoDate = dates.parseDateStringFromDateOrYear(params.value, {
              allowLocalFormats: true,
              defaultReturn: null,
            });

            return (
              <FormattedDatePicker
                value={isoDate}
                onChangeDate={(newDate) => {
                  if (newDate) {
                    const date = dates.parseDateValue(newDate);
                    const formattedDate = date.toLocaleDateString("en-US", {
                      month: "numeric",
                      day: "numeric",
                      year: "2-digit",
                    });
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === params.id
                          ? { ...r, effectiveDate: formattedDate }
                          : r
                      )
                    );
                  } else {
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === params.id ? { ...r, effectiveDate: "-" } : r
                      )
                    );
                  }
                }}
                compact
                slotProps={{
                  textField: {
                    variant: "standard",
                    size: "small",
                    fullWidth: true,
                    sx: EDIT_FIELD_SX,
                  },
                }}
              />
            );
          }
          return params.value;
        },
      },
      {
        field: "group",
        headerName: "Group",
        type: "string",
        width: 140,
        editable: false,
        renderCell: (params) => {
          if (params.id === GROUP_TOTALS_ROW_ID) {
            return params.value || "";
          }
          if (editing) {
            if (addingGroupRowId === params.id) {
              return (
                <Stack direction={"row"} spacing={1} alignItems={"center"}>
                  <MuiTextField
                    value={newRowGroupValue}
                    size={"small"}
                    autoFocus
                    variant={"standard"}
                    placeholder={"New group"}
                    onChange={(event) =>
                      setNewRowGroupValue(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleConfirmAddGroupForRow(params.id);
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        handleCancelAddGroupForRow();
                      }
                    }}
                    sx={EDIT_FIELD_SX}
                  />
                  <IconButton
                    size={"small"}
                    color={"primary"}
                    onClick={() => handleConfirmAddGroupForRow(params.id)}
                  >
                    <CheckIcon fontSize={"small"} />
                  </IconButton>
                  <IconButton
                    size={"small"}
                    color={"inherit"}
                    onClick={handleCancelAddGroupForRow}
                  >
                    <CloseIcon fontSize={"small"} />
                  </IconButton>
                </Stack>
              );
            }
            return (
              <FormControl variant={"standard"} size={"small"} fullWidth>
                <Select
                  value={params.value || ""}
                  displayEmpty
                  sx={EDIT_FIELD_SX}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "__ADD_NEW__") {
                      handleStartAddingGroupForRow(params.id, params.value);
                      return;
                    }
                    const normalized = normalizeGroupName(value);
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === params.id ? { ...r, group: normalized } : r
                      )
                    );
                  }}
                >
                  <MenuItem value={""}>{"None"}</MenuItem>
                  {groupOptions.map((groupName) => (
                    <MenuItem key={groupName} value={groupName}>
                      {groupName}
                    </MenuItem>
                  ))}
                  <MenuItem value={"__ADD_NEW__"}>
                    <Stack direction={"row"} spacing={1} alignItems={"center"}>
                      <AddIcon fontSize={"small"} />
                      <Typography variant={"body2"}>
                        {"Add new group"}
                      </Typography>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            );
          }
          return params.value || "";
        },
      },
      {
        ...makePrettyNumberColDef({
          field: "ulLimitMillions",
          headerName: "UL Limit (MM)",
          width: 90,
          dp: 0,
        }),
        editable: false,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? applyDerivedValues({ ...r, ulLimitMillions: value })
                        : r
                    )
                  );
                }}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "ulLimitMillions",
            dp: 0,
          }).renderCell(params);
        },
      },
      {
        ...makePrettyNumberColDef({
          field: "ueGlxPremiumPreviousYear",
          headerName: "UE GLX Prem (Prev Yr)",
          width: 120,
          dp: 0,
        }),
        editable: false,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? applyDerivedValues({
                            ...r,
                            ueGlxPremiumPreviousYear: value,
                          })
                        : r
                    )
                  );
                }}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "ueGlxPremiumPreviousYear",
            dp: 0,
          }).renderCell(params);
        },
      },
      {
        ...makePrettyNumberColDef({
          field: "ueGlxPremiumCurrentYear",
          headerName: "UE GLX Prem (Curr Yr)",
          width: 120,
          dp: 0,
        }),
        editable: false,
        headerClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        cellClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? applyDerivedValues({
                            ...r,
                            ueGlxPremiumCurrentYear: value,
                          })
                        : r
                    )
                  );
                }}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "ueGlxPremiumCurrentYear",
            dp: 0,
          }).renderCell(params);
        },
      },
      makePrettyNumberColDef({
        field: "ueGlxPpm",
        headerName: "UE GLX $/mil (Curr Yr)",
        width: 110,
        dp: 0,
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "ueGlxPremiumIncrease",
        headerName: "UE GLX YoY Increase",
        width: 120,
        dp: 1,
        suffix: "%",
        editable: false,
      }),
      {
        ...makePrettyNumberColDef({
          field: "ueEllPremiumPreviousYear",
          headerName: "UE ELL Prem (Prev Yr)",
          width: 120,
          dp: 0,
        }),
        editable: false,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? applyDerivedValues({
                            ...r,
                            ueEllPremiumPreviousYear: value,
                          })
                        : r
                    )
                  );
                }}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "ueEllPremiumPreviousYear",
            dp: 0,
          }).renderCell(params);
        },
      },
      {
        ...makePrettyNumberColDef({
          field: "ueEllPremiumCurrentYear",
          headerName: "UE ELL Prem (Curr Yr)",
          width: 120,
          dp: 0,
        }),
        editable: false,
        headerClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        cellClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? applyDerivedValues({
                            ...r,
                            ueEllPremiumCurrentYear: value,
                          })
                        : r
                    )
                  );
                }}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "ueEllPremiumCurrentYear",
            dp: 0,
          }).renderCell(params);
        },
      },
      makePrettyNumberColDef({
        field: "ueEllPpm",
        headerName: "UE ELL $/mil (Curr Yr)",
        width: 110,
        dp: 0,
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "ueEllPremiumIncrease",
        headerName: "UE ELL YoY Increase",
        width: 120,
        dp: 1,
        suffix: "%",
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "ueCombinedPremiumPreviousYear",
        headerName: "UE Comb Prem (Prev Yr)",
        width: 120,
        dp: 0,
        editable: false,
      }),
      {
        ...makePrettyNumberColDef({
          field: "ueCombinedPremiumCurrentYear",
          headerName: "UE Comb Prem (Curr Yr)",
          width: 120,
          dp: 0,
          editable: false,
        }),
        headerClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        cellClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
      },
      makePrettyNumberColDef({
        field: "ueCombinedUlPpm",
        headerName: "UE Comb UL $/mil (Curr Yr)",
        width: 120,
        dp: 0,
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "ueCombinedPremiumIncrease",
        headerName: "UE Comb YoY Increase",
        width: 120,
        dp: 1,
        suffix: "%",
        editable: false,
      }),
      {
        ...makePrettyNumberColDef({
          field: "munichRequiredPremium",
          headerName: "Munich req % UE UL ppm (5M)",
          width: 120,
          dp: 0,
          editable: false,
        }),
        headerClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        cellClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
      },
      makePrettyNumberColDef({
        field: "munichPpm",
        headerName: "Lead 5M x UE ppm",
        width: 110,
        dp: 0,
        editable: false,
      }),
      {
        ...makePrettyNumberColDef({
          field: "munichPpmRelativeToUl",
          headerName: "5M x UE, % UL ppm",
          width: 90,
          dp: 1,
          suffix: "%",
        }),
        editable: false,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? { ...r, munichPpmRelativeToUl: value }
                        : r
                    )
                  );
                }}
                suffix={"%"}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "munichPpmRelativeToUl",
            dp: 1,
            suffix: "%",
          }).renderCell(params);
        },
      },
      makePrettyNumberColDef({
        field: "evanstonPpm",
        headerName: "10M ppm @90% UL 5M x UE",
        width: 120,
        dp: 0,
        editable: false,
      }),
      {
        ...makePrettyNumberColDef({
          field: "evanstonPpmRelativeToUl",
          headerName: "10M x Prin, % UL ppm",
          width: 110,
          dp: 1,
          suffix: "%",
        }),
        editable: false,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? { ...r, evanstonPpmRelativeToUl: value }
                        : r
                    )
                  );
                }}
                suffix={"%"}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "evanstonPpmRelativeToUl",
            dp: 1,
            suffix: "%",
          }).renderCell(params);
        },
      },
      {
        ...makePrettyNumberColDef({
          field: "evanstonPremium",
          headerName: "10M prem @90% UL 5M x UE",
          width: 120,
          dp: 0,
          editable: false,
        }),
        headerClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        cellClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
      },
      makePrettyNumberColDef({
        field: "actualPremium15M",
        headerName: "Actual 15M prem",
        width: 120,
        dp: 0,
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "munichEvanstonBoundPremiumPreviousYear",
        headerName: "Previous Year Munich/Evanston Bound Prem",
        width: 120,
        dp: 0,
        editable: false,
      }),
      {
        ...makePrettyNumberColDef({
          field: "munichBoundPremiumPreviousYear",
          headerName: "Previous Year Munich Bound Prem",
          width: 120,
          dp: 0,
        }),
        editable: false,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? { ...r, munichBoundPremiumPreviousYear: value }
                        : r
                    )
                  );
                }}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "munichBoundPremiumPreviousYear",
            dp: 0,
          }).renderCell(params);
        },
      },
      {
        ...makePrettyNumberColDef({
          field: "evanstonBoundPremiumPreviousYear",
          headerName: "Previous Year Evanston Bound Prem",
          width: 120,
          dp: 0,
        }),
        editable: false,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? { ...r, evanstonBoundPremiumPreviousYear: value }
                        : r
                    )
                  );
                }}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "evanstonBoundPremiumPreviousYear",
            dp: 0,
          }).renderCell(params);
        },
      },
      makePrettyNumberColDef({
        field: "princetonPremiumIncrease",
        headerName: "5M Prin prem vs exp",
        width: 120,
        dp: 1,
        suffix: "%",
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "markelPremiumIncrease",
        headerName: "10M Markel prem vs exp",
        width: 120,
        dp: 1,
        suffix: "%",
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "ue15MPremiumIncrease",
        headerName: "Previous Year 15M x UE prem vs exp",
        width: 120,
        dp: 1,
        suffix: "%",
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "arkPpm",
        headerName: "5M Ark PPM xs Evanston",
        width: 110,
        dp: 0,
        editable: false,
      }),
      {
        ...makePrettyNumberColDef({
          field: "arkTotalPremium",
          headerName: "ARK Gross Prem (Curr Yr)",
          width: 120,
          dp: 0,
          editable: false,
        }),
        headerClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        cellClassName: CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS,
        renderCell: (params) => {
          if (editing) {
            return (
              <PrettyNumberTextField
                value={params.value}
                onChangeNumber={(value) => {
                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.id
                        ? applyDerivedValues({
                            ...r,
                            arkTotalPremium: value,
                          })
                        : r
                    )
                  );
                }}
                variant={"standard"}
                size={"small"}
                fullWidth
                sx={{ marginTop: "0.8rem" }}
              />
            );
          }
          return makePrettyNumberColDef({
            field: "arkTotalPremium",
            dp: 0,
          }).renderCell(params);
        },
      },
      makePrettyNumberColDef({
        field: "arkRelativeToEvanston",
        headerName: "Ark vs Evanston $10M (avg 98%)",
        width: 120,
        dp: 2,
        suffix: "%",
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "arkErp",
        headerName: "Ark ERP",
        width: 120,
        dp: 0,
        editable: false,
      }),
      makePrettyNumberColDef({
        field: "totalPremiumWithSerp",
        headerName: "Total Prem + SERP",
        width: 120,
        dp: 0,
        editable: false,
      }),
    ];
  }, [
    addingGroupRowId,
    editing,
    groupOptions,
    handleCancelAddGroupForRow,
    handleConfirmAddGroupForRow,
    handleInsuredNameCommit,
    handleOpenDeleteConfirm,
    handleStartAddingGroupForRow,
    newRowGroupValue,
  ]);

  const yearOptions = useMemo(() => {
    const yearSet = new Set();
    rows.forEach((row) => {
      if (row.effectiveYear) yearSet.add(row.effectiveYear);
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [rows]);

  const filteredBaseRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesYear =
        selectedYear == null || row.effectiveYear === selectedYear;
      if (!matchesYear) {
        return false;
      }
      if (selectedGroup == null) {
        return true;
      }
      return normalizeGroupName(row.group) === selectedGroup;
    });
  }, [rows, selectedGroup, selectedYear]);

  const filteredRows = useMemo(() => {
    if (selectedGroup == null || editing) {
      return filteredBaseRows;
    }
    return [
      ...filteredBaseRows,
      buildGroupTotalsRow(filteredBaseRows, selectedGroup),
    ];
  }, [editing, filteredBaseRows, selectedGroup]);

  return (
    <>
      <Component hideTitleLoadingEllipsis={true}>
        <Typography variant={"h6"} sx={{ pb: 2 }}>
          {"CAUSE"}
        </Typography>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          sx={{ mb: 2 }}
        >
          <Stack direction={"row"} spacing={2} alignItems={"center"}>
            <InlineSearch
              placeholder={"Quick Search"}
              onChange={(searchText) => setSearchQuery(searchText)}
            />
            <FormControl size={"small"} sx={{ minWidth: 140 }}>
              <InputLabel>{"Year"}</InputLabel>
              <Select
                value={selectedYear ?? "ALL"}
                label={"Year"}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedYear(value === "ALL" ? null : value);
                }}
              >
                <MenuItem value={"ALL"}>{"All Years"}</MenuItem>
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size={"small"} sx={{ minWidth: 180 }}>
              <InputLabel>{"Group"}</InputLabel>
              <Select
                value={selectedGroup ?? "ALL"}
                label={"Group"}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedGroup(value === "ALL" ? null : value);
                }}
              >
                <MenuItem value={"ALL"}>{"All Groups"}</MenuItem>
                {groupOptions.map((groupName) => (
                  <MenuItem key={groupName} value={groupName}>
                    {groupName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={isFullWidth}
                  onChange={(e) => onToggleFullWidth(e.target.checked)}
                  size={"small"}
                />
              }
              label={"Full width"}
            />
          </Stack>
          <Stack direction={"row"} spacing={2}>
            {editing && (
              <Button
                variant={"outlined"}
                color={"secondary"}
                onClick={() => {
                  setRows(JSON.parse(JSON.stringify(originalRows)));
                  setEditing(false);
                }}
                startIcon={<CancelIcon />}
                disabled={isSaving}
                sx={{
                  width: "110px",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  color: "grey",
                }}
              >
                {"Cancel"}
              </Button>
            )}
            <Button
              variant={editing ? "outlined" : "contained"}
              disableElevation
              color={"primary"}
              onClick={async () => {
                if (editing) {
                  await handleSaveChanges();
                }
                setEditing(!editing);
              }}
              startIcon={editing ? <DoneIcon /> : <EditIcon />}
              disabled={isSaving}
              sx={{
                width: "110px",
                textTransform: "none",
                whiteSpace: "nowrap",
                color: editing ? "darkorange" : null,
              }}
            >
              {isSaving ? "Saving..." : editing ? "Done" : "Edit"}
            </Button>
            <Button
              variant={"contained"}
              color={"primary"}
              sx={{ width: 140, boxShadow: "none !important" }}
              onClick={handleOpenCreate}
            >
              {"+ Add"}
            </Button>
          </Stack>
        </Stack>

        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowClassName={(params) =>
            params.id === GROUP_TOTALS_ROW_ID ? "cause-workbook-totals-row" : ""
          }
          loading={isLoadingSubmissions || isLoadingRows}
          disableRowSelectionOnClick
          processRowUpdate={(newRow) => {
            setRows((prev) =>
              prev.map((r) => (r.id === newRow.id ? newRow : r))
            );
            return newRow;
          }}
          filterModel={{
            items: [],
            quickFilterValues: searchQuery.split(" ").filter((q) => q !== ""),
          }}
          initialState={{
            density: "compact",
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            marginTop: 3,
            height: "calc(100vh - 280px)",
            "& .MuiDataGrid-toolbarContainer": {
              paddingLeft: 0,
              paddingRight: 0,
            },
            "& .MuiDataGrid-columnHeader": {
              whiteSpace: "normal",
              lineHeight: "normal",
              justifyContent: "center",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "1.2",
              overflow: "visible",
              textOverflow: "clip",
              textAlign: "center",
            },
            "& .MuiDataGrid-cell .MuiTextField-root": {
              marginTop: "0 !important",
            },
            "& .MuiDataGrid-cell .MuiFormControl-root": {
              marginTop: "0 !important",
            },
            "& .MuiDataGrid-cell .MuiInputBase-root": {
              fontSize: "inherit",
              lineHeight: "inherit",
              height: "100%",
              alignItems: "center",
              transform: "translateY(2px)",
            },
            "& .MuiDataGrid-cell .MuiInputBase-input": {
              fontSize: "inherit",
              lineHeight: "inherit",
              paddingTop: "3px",
              paddingBottom: "1px",
            },
            [`& .${CURRENT_YEAR_PREMIUM_HIGHLIGHT_CLASS}`]: {
              backgroundColor: "rgba(255, 243, 224, 0.7)",
            },
            "& .cause-workbook-totals-row": {
              fontWeight: 700,
              backgroundColor: "rgba(232, 245, 233, 0.55)",
            },
          }}
        />

        <CauseCreateRecordDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSuccess={(savedSubmission) => {
            refetchSubmissions();

            const priorSubmission = savedSubmission?.priorSubmission || null;
            const savedRow = extractDataFromSubmission(
              savedSubmission,
              priorSubmission
            );
            if (!savedRow) return;
            pendingCreatedRowsRef.current.set(savedRow.id, savedRow);
            setRows((prev) => {
              const existingIdx = prev.findIndex((r) => r.id === savedRow.id);
              if (existingIdx >= 0) {
                return prev.map((r) => (r.id === savedRow.id ? savedRow : r));
              }
              return [savedRow, ...prev];
            });
            setOriginalRows((prev) => {
              const existingIdx = prev.findIndex((r) => r.id === savedRow.id);
              if (existingIdx >= 0) {
                return prev.map((r) => (r.id === savedRow.id ? savedRow : r));
              }
              return [savedRow, ...prev];
            });
          }}
        />
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCloseDeleteConfirm}
          maxWidth={"sm"}
          fullWidth
        >
          <DialogTitle>{"Delete Submission"}</DialogTitle>
          <DialogContent>
            <Typography variant={"body1"}>
              {
                "Are you sure you want to delete this submission? This action cannot be undone."
              }
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleConfirmDelete}
              variant={"contained"}
              color={"error"}
            >
              {"Delete"}
            </Button>
            <Button
              onClick={handleCloseDeleteConfirm}
              variant={"contained"}
              color={"secondary"}
            >
              {"Cancel"}
            </Button>
          </DialogActions>
        </Dialog>
      </Component>
    </>
  );
};

export default CauseWorkbookPage;
